/**
 * TAHLEEL.ai — Dashboard (clean, professional)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload, Clock, CheckCircle, TrendingUp,
  ChevronRight, Loader2, BarChart3
} from 'lucide-react';
import { getAnalysisHistory } from '../services/api';

const Dashboard = ({ onNavigateToUpload, onNavigateToHistory, onViewAnalysis }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    total: 0, completed: 0, avgTime: 0, avgConf: 0, recent: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await getAnalysisHistory(20);
      if (res.success && res.analyses) {
        const all = res.analyses;
        const done = all.filter(a => a.status === 'completed');
        const totalTime = done.reduce((s, a) => s + (a.processing_time_seconds || 0), 0);
        const totalConf = done.reduce((s, a) => s + (a.confidence_score || 0), 0);
        setStats({
          total: all.length,
          completed: done.length,
          avgTime: done.length > 0 ? Math.round(totalTime / done.length) : 0,
          avgConf: done.length > 0 ? Math.round(totalConf / done.length) : 0,
          recent: all.slice(0, 5)
        });
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* STATS ROW */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={BarChart3} label="Total Analyses" value={stats.total} color="blue" />
        <StatCard icon={CheckCircle} label="Completed" value={stats.completed} color="emerald" />
        <StatCard icon={Clock} label="Avg. Time" value={`${stats.avgTime}s`} color="purple" />
        <StatCard icon={TrendingUp} label="Avg. Confidence" value={`${stats.avgConf}%`} color="amber" />
      </div>

      {/* QUICK ACTIONS */}
      <div className="flex gap-3">
        <button onClick={onNavigateToUpload}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all"
          style={{ background: '#f59e0b', color: '#0a1628' }}>
          <Upload className="h-4 w-4" />
          Upload Video
        </button>
        <button onClick={onNavigateToHistory}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all"
          style={{ background: '#0a1628', color: '#fff' }}>
          View History
        </button>
      </div>

      {/* RECENT ANALYSES */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">Recent Analyses</h3>
          <button onClick={onNavigateToHistory}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
            View All <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {stats.recent.length === 0 ? (
          <div className="text-center py-12 px-6">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <BarChart3 className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm font-semibold text-gray-900 mb-1">No analyses yet</p>
            <p className="text-xs text-gray-500 mb-4">Upload your first match video to get started</p>
            <button onClick={onNavigateToUpload}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-all">
              Upload Video
            </button>
          </div>
        ) : (
          <div>
            {stats.recent.map((a, i) => (
              <div key={a.id}
                onClick={() => onViewAnalysis(a.id)}
                className={`flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors ${
                  i < stats.recent.length - 1 ? 'border-b border-gray-50' : ''
                }`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    a.status === 'completed' ? 'bg-emerald-50' : 'bg-gray-100'
                  }`}>
                    <CheckCircle className={`h-4 w-4 ${
                      a.status === 'completed' ? 'text-emerald-500' : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{a.opponent_team || 'Match Analysis'}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                {a.confidence_score > 0 && (
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg flex-shrink-0">
                    {a.confidence_score}%
                  </span>
                )}
                <ChevronRight className="h-4 w-4 text-gray-300 ml-3 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
};

export default Dashboard;
